// The registry behind the design gallery (docs/design-system.md 6.2). A
// component enters the system by adding an entry here; design/registry.test.ts
// fails when an export of a system file has none. The gallery shows the code as
// it stands, known-bad cases included; nothing here changes a component.
import { useState, type ComponentType, type ReactNode } from 'react'
import {
  Button,
  Empty,
  Failed,
  Field,
  IconButton,
  PageTitle,
  Segmented,
  SectionTitle,
  Sheet,
  SideSheet,
  Tag,
} from '@/components/ui'
import { TaskRow } from '@/components/TaskRow'
import { ActionBar, type ActionItem } from '@/components/ActionBar'
import { FolderInput, Pin, Share2 } from 'lucide-react'
import { toneClass, type Tone } from '@/lib/colour'
import { FIXTURE_TASKS } from './fixtures'
import { PrimeMark } from '@/components/PrimeMark'
import { PRIME_CANDIDATES, PrimeCandidates } from './primeCandidates'

export type Section = 'Primitives' | 'Layout' | 'Overlays' | 'Forms' | 'Domain'
export const SECTIONS: Section[] = ['Primitives', 'Layout', 'Overlays', 'Forms', 'Domain']

export interface Example {
  label: string
  render: () => ReactNode
  /** measure the first button in it against the 44px phone minimum (D3) */
  tap?: boolean
}

export interface Entry {
  id: string
  name: string
  section: Section
  /** the export it documents, from a system file */
  exportName?: string
  component?: ComponentType<never>
  /** a class in index.css rather than a component */
  css?: string
  job: string
  replaces?: string[]
  examples: Example[]
}

/** Files whose exports must each have an entry. */
export const SYSTEM_FILES = ['components/ui.tsx'] as const
/** Exports of a system file that are not components (the class joiner, the card-action chip classes for links). */
export const NOT_COMPONENTS = ['cx', 'CHIP_CLASS']

const BAR_ITEMS: ActionItem[] = [
  { id: 'pin', label: 'Pinned', icon: Pin, pin: true, pressed: true, onSelect: () => {} },
  { id: 'move', label: 'Move…', icon: FolderInput, onSelect: () => {} },
  { id: 'share', label: 'Share…', icon: Share2, onSelect: () => {} },
  {
    id: 'delete',
    label: 'Delete',
    tone: 'danger',
    onSelect: () => {},
    confirm: { label: 'Delete it' },
  },
]

const OpenSheet = ({ side }: { side?: boolean }) => {
  const [open, setOpen] = useState(false)
  const Shell = side ? SideSheet : Sheet
  return (
    <>
      <Button onClick={() => setOpen(true)}>Open the {side ? 'side sheet' : 'sheet'}</Button>
      <Shell open={open} onClose={() => setOpen(false)} title="Example">
        <p className="text-sm text-muted">Header, scrolling body, optional footer.</p>
      </Shell>
    </>
  )
}

const SegmentedDemo = () => {
  const [v, setV] = useState('day')
  return (
    <Segmented
      label="Range"
      value={v}
      onChange={setV}
      options={[
        { value: 'day', label: 'Day' },
        { value: 'week', label: 'Week' },
        { value: 'month', label: 'Month' },
      ]}
    />
  )
}

const row = (children: ReactNode) => <div className="flex flex-wrap items-center gap-2">{children}</div>

export const REGISTRY: Entry[] = [
  {
    id: 'button',
    name: 'Button',
    section: 'Primitives',
    exportName: 'Button',
    component: Button as ComponentType<never>,
    job: 'Every labelled action. Primary once per view; chips for filters and triage.',
    replaces: ['hand-typed chip recipes', 'text-rose-300 className on a ghost button (danger)'],
    examples: [
      {
        label: 'Variants',
        tap: true,
        render: () =>
          row(
            <>
              <Button variant="primary">Primary</Button>
              <Button>Ghost</Button>
              <Button variant="quiet">Quiet</Button>
              <Button variant="danger">Danger</Button>
            </>,
          ),
      },
      {
        label: 'Chips',
        tap: true,
        render: () =>
          row(
            <>
              <Button variant="chip">Chip</Button>
              <Button variant="chipOn">Chip on</Button>
            </>,
          ),
      },
      { label: 'Big (the 44px size)', tap: true, render: () => <Button big>Big</Button> },
      { label: 'Disabled', render: () => <Button disabled>Disabled</Button> },
    ],
  },
  {
    id: 'icon-button',
    name: 'IconButton',
    section: 'Primitives',
    exportName: 'IconButton',
    component: IconButton as ComponentType<never>,
    job: 'A glyph-only action; `label` is the aria-label and the tooltip.',
    examples: [{ label: 'Default', tap: true, render: () => <IconButton label="More">⋯</IconButton> }],
  },
  {
    id: 'tag',
    name: 'Tag',
    section: 'Primitives',
    exportName: 'Tag',
    component: Tag as ComponentType<never>,
    job: 'The one badge: a tone from lib/colour.ts, or an inline style for label hues.',
    examples: [
      {
        label: 'Every tone',
        render: () =>
          row(
            (Object.keys(toneClass) as Tone[]).map((t) => (
              <Tag key={t} tone={t}>
                {t}
              </Tag>
            )),
          ),
      },
    ],
  },
  {
    id: 'empty',
    name: 'Empty',
    section: 'Layout',
    exportName: 'Empty',
    component: Empty as ComponentType<never>,
    job: 'A page with nothing in it. Too tall for a card or a widget.',
    examples: [{ label: 'Default', render: () => <Empty icon="✓">Nothing here yet.</Empty> }],
  },
  {
    id: 'failed',
    name: 'Failed',
    section: 'Layout',
    exportName: 'Failed',
    component: Failed as ComponentType<never>,
    job: 'A read from the server that failed: what, what to do, the reason, Try again. Every online-only page, and compact in a widget.',
    replaces: ['a bare error string in rose, amber or grey'],
    examples: [
      {
        label: 'Page',
        render: () => <Failed what="the builds" reason="upstream: 424" onRetry={() => {}} />,
      },
      { label: 'Widget', render: () => <Failed compact what="the weather" onRetry={() => {}} /> },
    ],
  },
  {
    id: 'page-title',
    name: 'PageTitle',
    section: 'Layout',
    exportName: 'PageTitle',
    component: PageTitle as ComponentType<never>,
    job: 'The page heading, with an optional eyebrow and a trailing control.',
    examples: [
      { label: 'Title', render: () => <PageTitle>Today</PageTitle> },
      {
        label: 'Eyebrow and right',
        render: () => (
          <PageTitle eyebrow="Friday 10 October" right={<Button variant="chip">Edit</Button>}>
            Today
          </PageTitle>
        ),
      },
    ],
  },
  {
    id: 'section-title',
    name: 'SectionTitle',
    section: 'Layout',
    exportName: 'SectionTitle',
    component: SectionTitle as ComponentType<never>,
    job: 'A sentence-case label over a group. 13px semibold, never tracked caps.',
    examples: [
      {
        label: 'With a right slot',
        render: () => <SectionTitle right={<Button variant="quiet">All</Button>}>Upcoming</SectionTitle>,
      },
    ],
  },
  {
    id: 'card',
    name: '.card',
    section: 'Layout',
    css: '.card',
    job: 'A group with a hairline edge and no shadow.',
    examples: [
      {
        label: 'Card',
        render: () => <div className="card p-3 text-sm">A hairline edge, token radius, no shadow.</div>,
      },
    ],
  },
  {
    id: 'group-list',
    name: '.group-list',
    section: 'Layout',
    css: '.group-list',
    job: 'Rows in one surface divided by hairlines, not a card per row.',
    examples: [
      {
        label: 'Three rows',
        render: () => (
          <ul className="group-list">
            {['Inbox', 'Today', 'Upcoming'].map((t) => (
              <li key={t} className="px-3 py-2.5 min-h-12 flex items-center text-sm">
                {t}
              </li>
            ))}
          </ul>
        ),
      },
    ],
  },
  {
    id: 'sheet',
    name: 'Sheet',
    section: 'Overlays',
    exportName: 'Sheet',
    component: Sheet as ComponentType<never>,
    job: 'Bottom sheet on a phone, centred dialog from md. The only floating surface that carries --shadow-pop.',
    examples: [{ label: 'Open it', render: () => <OpenSheet /> }],
  },
  {
    id: 'side-sheet',
    name: 'SideSheet',
    section: 'Overlays',
    exportName: 'SideSheet',
    component: SideSheet as ComponentType<never>,
    job: 'The same shell from the side: a list beside what you are doing.',
    examples: [{ label: 'Open it', render: () => <OpenSheet side /> }],
  },
  {
    id: 'field',
    name: 'Field',
    section: 'Forms',
    exportName: 'Field',
    component: Field as ComponentType<never>,
    job: 'A label, a control, a hint or an error. Raw inputs take their frame from index.css.',
    examples: [
      {
        label: 'Hint',
        render: () => (
          <Field label="Title" hint="Shown in lists">
            <input defaultValue="Book the dentist" />
          </Field>
        ),
      },
      {
        label: 'Error',
        render: () => (
          <Field label="Title" error="A title is required">
            <input defaultValue="" />
          </Field>
        ),
      },
    ],
  },
  {
    id: 'segmented',
    name: 'Segmented',
    section: 'Forms',
    exportName: 'Segmented',
    component: Segmented as ComponentType<never>,
    job: 'An exclusive choice as a radiogroup pill.',
    replaces: ['about 20 hand-rolled radio groups'],
    examples: [{ label: 'Three options', tap: true, render: () => <SegmentedDemo /> }],
  },
  {
    id: 'task-row',
    name: 'TaskRow',
    section: 'Domain',
    component: TaskRow as ComponentType<never>,
    job: 'A task in a list: tick, title, meta, actions. Stays its own component.',
    examples: [
      {
        label: 'Plain, long, overdue, done',
        render: () => (
          <ul className="group-list">
            <TaskRow task={FIXTURE_TASKS.plain} />
            <TaskRow task={FIXTURE_TASKS.long} />
            <TaskRow task={FIXTURE_TASKS.overdue} />
            <TaskRow task={FIXTURE_TASKS.done} />
          </ul>
        ),
      },
      {
        label: 'Triage',
        render: () => (
          <ul className="group-list">
            <TaskRow task={FIXTURE_TASKS.plain} mode="triage" />
          </ul>
        ),
      },
    ],
  },
  {
    id: 'action-bar',
    name: 'ActionBar',
    section: 'Domain',
    component: ActionBar as ComponentType<never>,
    job: 'The actions in a header. Wide: one quiet toolbar, every action inline in hairline groups (state, operations, people), Delete apart and last, state tinted. Phone: pinned actions and …, the rest in a sheet.',
    examples: [
      { label: 'Wide toolbar', render: () => <ActionBar items={BAR_ITEMS} phone={false} label="Example actions" /> },
      { label: 'Phone line', tap: true, render: () => <ActionBar items={BAR_ITEMS} phone label="Example actions" /> },
    ],
  },
  {
    id: 'prime-mark',
    name: 'PrimeMark',
    section: 'Primitives',
    component: PrimeMark as ComponentType<never>,
    job: 'Prime’s icon: one colour, wherever Prime is meant. A is the mark in use; B and C were drawn and not chosen.',
    examples: PRIME_CANDIDATES.map((c) => ({
      label: `${c.name}: ${c.note}`,
      render: () => <PrimeCandidates candidate={c} />,
    })),
  },
]
